import React, { useEffect, useState, useCallback } from 'react';
import styles from './appPlaceholder.module.scss';
import logoUrl from '../../assets/img/dark_logo.png';
import InfoIcon from './info.svg?react';
import CloseIcon from 'src/assets/icons/close.svg?react';
import UpdatedIcon from 'src/assets/icons/check_mark.svg?react';
import SuccessIcon from 'src/assets/icons/toast-success.svg?react';
import ErrorIcon from 'src/assets/icons/toast-error.svg?react';
import WarningIcon from 'src/assets/icons/toast-info.svg?react';
import { ToastContext} from '../../hooks/useToast';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

type Props = {
  children: React.ReactNode;
};

const messageWidth = (messageLength: number) => {
  if (messageLength < 10) {
    return messageLength * 20; 
  } else if (messageLength < 20) {
    return messageLength * 15;
  } else if (messageLength < 30) {
    return messageLength * 12.5;
  }
  return messageLength * 10;
}
const eligiblePages: string[] = ['/feedbackform', '/ff', '/parent'];

function AppPlaceholder({ children }: Props) {
  const location = document.location;
  const [appWidth, setAppWidth] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [toastWidth, setToastWidth] = useState(320);

  const UpdateMsg = useCallback(({ message, view }) => (
    <div className={styles.toastView}>
      {message}
      {view}
      <CloseIcon width={12} height={12} onClick={() => toast.dismiss()}/>
    </div>
  ), []);

  const ColorThemeMsg = ({ message }) => (
    <div className={styles.toastColorView}>
      {message}
      <CloseIcon width={12} height={12} onClick={() => toast.dismiss()}/>
    </div>
  );

  const updateToast = useCallback((messageText: string, subView?: React.ReactNode) => {
    toast(<UpdateMsg message={messageText} view={subView} />, {
      style: {
        width: 364,
        height: 46,
        minHeight: 46,
        padding: '0 8px',
        borderRadius: 10,
      },
      icon: <UpdatedIcon />,
    });
  }, [UpdateMsg]);

  const successToast = useCallback((messageText: string) => {
    setToastWidth(messageWidth(messageText.length));
    toast(<ColorThemeMsg message={messageText} />, {
      style: {
        width: messageWidth(messageText.length),
        height: 46,
        minHeight: 46,
        padding: '0 8px',
        borderRadius: 10,
        background: '#6BC690'
      },
      icon: <SuccessIcon />,
    });
  }, []);

  const warningToast = useCallback((messageText: string) => {
    setToastWidth(messageWidth(messageText.length));
    toast(<ColorThemeMsg message={messageText} />, {
      style: {
        width: messageWidth(messageText.length),
        height: 46,
        minHeight: 46,
        padding: '0 8px',
        borderRadius: 10,
        background: '#AF77B8'
      },
      icon: <WarningIcon />,
    });
  }, []);

  const errorToast = useCallback((messageText: string) => {
    setToastWidth(messageWidth(messageText.length));
    toast(<ColorThemeMsg message={messageText} />, {
      style: {
        width: messageWidth(messageText.length),
        height: 46,
        minHeight: 46,
        padding: '0 8px',
        borderRadius: 10,
        background: '#FF656E'
      },
      icon: <ErrorIcon />,
    });
  }, []);

  useEffect(() => {
    const listener = () => {
      const width = window.innerWidth
        || document.documentElement.clientWidth
        || document.body.clientWidth;

      setAppWidth(width);
      setLoading(false);
    };

    window.addEventListener('resize', listener);

    listener();
    return () => {
      window.removeEventListener('resize', listener);
    };
  }, []);

  if (loading) return null;

  const isEligible = eligiblePages.some(page => location.pathname.startsWith(page));

  if (!isEligible && appWidth !== undefined && appWidth < 480) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.logoWrapper}>
          <img className={styles.logo} src={logoUrl} alt="Logo"/>
        </div>
        <div className={styles.content}>
          <div className={styles.placeholder}>
            <InfoIcon className={styles.icon}/>
            <div className={styles.text}>
              Your browser window is too small. Please increase the size of your browser window to the full width of your screen. You may also need to check your Zoom settings in your Browser. To do this in Google Chrome, click on the View menu, and select "Actual Size" from the drop down list. Our platform is not compatible with small screens or mobile devices at this time. Please also ensure you are using Google Chrome.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ToastContext.Provider value={{updateToast, successToast, warningToast, errorToast}}>
      {children}
      <ToastContainer autoClose={5000} pauseOnFocusLoss={false} hideProgressBar={true} position="bottom-center" closeButton={false} pauseOnHover={true} style={{width: toastWidth}} />
    </ToastContext.Provider>
  );
}

export default AppPlaceholder;
